/**
 * TrainingPage — the team's training runs, read from the data bucket
 * through /api/training-runs. A reward run shows its reward curve with
 * run interventions, the health cards and the most recently trained
 * rollout groups. An SFT run (every reward field null, TRA-1332) shows
 * its loss curve and the health cards it measures. The newest run
 * renders; the exporter re-writes its document as the run advances.
 *
 * Future (2026-08-21): surface the trained model VERSIONS here too — the
 * checkpoint lineage (v5s30 … hye3 for Cartage) currently lives only on
 * the dashboard's training-history band and the eval group's run
 * dropdown.
 */

import { useState } from 'react'

import { Activity, Flag } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import type { TrainingLaunchTargetView, TrainingLaunchView, TrainingRunDocument } from '@/api/generated/model'
import { DEPLOYMENT } from '@/config'
import { LoadingPanel } from '@/components/shared/LoadingScreen'
import { DefaultGate, ScoreHeldOutButton } from '@/components/training/DefaultGate'
import { CardLabel, HealthCards } from '@/components/training/HealthCards'
import { heldOutScore } from '@/components/training/heldOutScore'
import { LaunchRunDialog } from '@/components/training/LaunchRunDialog'
import { TrainingDataMenu } from '@/components/training/TrainingDataMenu'
import { ValidationCurve } from '@/components/training/ValidationCurve'
import {
  LAUNCH_STATUS_LABEL,
  launchBlockedReason,
  launchLabel,
  launchSourcesAnswered,
  formatDuration,
  grpoChangesLine,
  grpoContinuation,
  isGrpoRun,
  launchWithoutMetrics,
  presetChangesLine,
  recordableAdapter,
  servableStep,
} from '@/components/training/launch'
import type { GrpoContinuation } from '@/components/training/launch'
import { LossCurve } from '@/components/training/LossCurve'
import { RewardCurve } from '@/components/training/RewardCurve'
import { RolloutGroups } from '@/components/training/RolloutGroups'
import { RolloutProgress } from '@/components/training/RolloutProgress'
import { DeploymentValue } from '@/components/training/ServeStepButton'
import { SessionsTab } from '@/components/training/sessions/SessionsTab'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { useTeam } from '@/contexts/TeamContext'
import { useMinimumLoading } from '@/hooks/useMinimumSplash'
import {
  useReportTrainingRun,
  useTrainingRuns,
  useStopTrainingRun,
  useTrainingGrpoOptions,
  useTrainingSetOptions,
} from '@/hooks/useTrainingRuns'

const STATUS_BADGE: Record<
  TrainingRunDocument['status'],
  { label: string; variant: 'success' | 'secondary' | 'destructive' | 'outline' }
> = {
  running: { label: 'Training', variant: 'secondary' },
  completed: { label: 'Completed', variant: 'success' },
  failed: { label: 'Failed', variant: 'destructive' },
  stopped: { label: 'Stopped', variant: 'outline' },
}

function formatElapsed(run: TrainingRunDocument): string {
  return formatDuration(new Date(run.updated_at).getTime() - new Date(run.started_at).getTime())
}


/** Run switcher for the title row; renders only when the team has more than one. */
function RunPicker({
  runs,
  selectedId,
  onSelect,
}: {
  runs: TrainingRunDocument[]
  selectedId: string
  onSelect: (runId: string) => void
}): React.JSX.Element {
  return (
    <Select value={selectedId} onValueChange={onSelect}>
      <SelectTrigger aria-label="Switch run" className="w-auto gap-2">
        <span className="font-mono text-xs uppercase tracking-wider">Switch run</span>
      </SelectTrigger>
      <SelectContent align="end">
        {runs.map((run) => (
          <SelectItem key={run.run_id} value={run.run_id}>
            <span className="flex items-baseline gap-2">
              <span>{run.display_name}</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {STATUS_BADGE[run.status].label}
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="space-y-1 border border-border p-4">
      <CardLabel>{label}</CardLabel>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  )
}

/** What the run produced: exported artifacts and deployment for every run,
 * the validation and held-out scores of a grpo run, and the task and eval of
 * a past run that trained one variant. */
function Outcome({
  run,
  grpo,
  servable,
  recordable,
  canApprove,
}: {
  run: TrainingRunDocument
  /** A grpo launch made the run (`isGrpoRun`): only those are scored on held-out tests. */
  grpo: boolean
  /** The step staff may serve when the run's own export failed (`servableStep`). */
  servable: string | null
  /** Whether staff may record the run's adapter served in the cluster (`recordableAdapter`). */
  recordable: boolean
  canApprove: boolean
}): React.JSX.Element {
  const task = run.task
  const evaluation = run.eval
  const promoted = run.promoted_to
  const score = heldOutScore(run)
  const heldOut = run.held_out_eval
  const description =
    task !== null
      ? 'The run trained on one variant and was scored on its base test.'
      : grpo
        ? 'See the checkpoints the run kept and how the served one scores on the held-out tests the night uses.'
        : 'See the checkpoints the run kept and its deployment.'
  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <h2 className="font-serif text-lg text-foreground">Outcome</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
        {grpo && (
          <Fact label="Validation score">
            {score === null ? (
              'Not scored'
            ) : (
              <span>
                {score.reward.toLocaleString()} at{' '}
                <span className="font-mono text-xs">{score.artifact}</span>
                {score.start !== null && `. Started at ${score.start.toLocaleString()}.`}
              </span>
            )}
          </Fact>
        )}
        {grpo && (
          <Fact label="Held-out eval">
            {heldOut == null ? (
              promoted === null ? (
                'Needs a served step'
              ) : canApprove && run.status === 'completed' ? (
                <ScoreHeldOutButton
                  runId={run.run_id}
                  label="Score on held-out tests"
                  scoredAt={null}
                />
              ) : (
                'Not scored yet'
              )
            ) : (
              <span>
                {heldOut.score.toLocaleString()} over {heldOut.tasks.toLocaleString()} of{' '}
                {heldOut.held_out.toLocaleString()} held-out tests
                {heldOut.baseline !== null && heldOut.baseline_at !== null
                  ? `. The night of ${heldOut.baseline_at.slice(0, 10)} scored ${heldOut.baseline.toLocaleString()}.`
                  : '. No nightly score to read it against yet.'}
              </span>
            )}
          </Fact>
        )}
        {task !== null && (
          <Fact label="Training task">
            <span className="font-mono text-xs">
              {task.base_stem} {task.variant}
            </span>
          </Fact>
        )}
        {task !== null && (
          <Fact label="Held-out eval">
            {evaluation === null ? (
              'Not run yet'
            ) : (
              <span>
                <span className="font-mono text-xs">{evaluation.test_id}</span> reward{' '}
                {evaluation.reward.toLocaleString()},{' '}
                <span className={evaluation.passed ? 'text-success' : 'text-destructive'}>
                  {evaluation.passed ? 'passed' : 'failed'}
                </span>
              </span>
            )}
          </Fact>
        )}
        <Fact label="Artifacts">
          {run.artifacts.length === 0 ? (
            'None yet'
          ) : (
            <ul className="space-y-1">
              {run.artifacts.map((artifact) => (
                <li key={artifact.name} className="font-mono text-xs">
                  {artifact.name} ({artifact.kind})
                  {artifact.eval_reward !== null ? ` ${artifact.eval_reward.toLocaleString()}` : ''}
                  {artifact.name === run.best_artifact ? ' best' : ''}
                </li>
              ))}
            </ul>
          )}
        </Fact>
        <Fact label="Deployment">
          <DeploymentValue
            runId={run.run_id}
            promoted={promoted}
            servable={servable}
            recordable={recordable}
            canApprove={canApprove}
          />
        </Fact>
      </div>
      {heldOut != null && (
        <DefaultGate runId={run.run_id} heldOut={heldOut} canApprove={canApprove} />
      )}
    </section>
  )
}

const LAUNCH_BADGE: Record<
  TrainingLaunchView['status'],
  'success' | 'secondary' | 'destructive' | 'outline'
> = { running: 'secondary', completed: 'success', failed: 'destructive', stopped: 'outline' }

interface StopCopy {
  title: string
  description: string
  confirm: string
}

const RUN_STOP: StopCopy = {
  title: 'Stop this run?',
  description:
    'The node gets about 30 seconds to record the stop and upload its logs before it shuts down. A GRPO run can be continued later from the last checkpoint it saved.',
  confirm: 'Stop run',
}

const SERVE_STOP: StopCopy = {
  title: 'Stop this serve?',
  description: 'The node shuts down within a few minutes. Baseten keeps any deployment the node made.',
  confirm: 'Stop serve',
}

/** Staff-only: stop the run or serve in flight, after a confirmation. */
function StopButton({
  runId,
  kind,
}: {
  runId: string
  kind: TrainingLaunchView['kind']
}): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const stop = useStopTrainingRun()
  const copy = kind === 'serve' ? SERVE_STOP : RUN_STOP
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(true)
        }}
      >
        Stop
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={stop.isPending}
              onClick={() => {
                setOpen(false)
              }}
            >
              Keep running
            </Button>
            <Button
              variant="destructive"
              disabled={stop.isPending}
              onClick={() => {
                stop.mutate(
                  { runId },
                  {
                    onSuccess: () => {
                      setOpen(false)
                    },
                  },
                )
              }}
            >
              {stop.isPending ? 'Stopping...' : copy.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** The run in flight: its latest node report, refreshed with the poll. */
function ActiveLaunch({
  launch,
  canStop,
}: {
  launch: TrainingLaunchView
  canStop: boolean
}): React.JSX.Element {
  // Measured to the node's last report, not the clock: deterministic per poll.
  const elapsed = formatDuration(
    new Date(launch.updated_at).getTime() - new Date(launch.created_at).getTime(),
  )
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-baseline gap-3 border border-border p-3"
    >
      <Badge variant={LAUNCH_BADGE.running}>{LAUNCH_STATUS_LABEL.running}</Badge>
      <span className="font-mono text-xs">{launchLabel(launch)}</span>
      {launch.message !== null && (
        <span className="text-sm text-muted-foreground">{launch.message}</span>
      )}
      <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {elapsed} at last report
      </span>
      {canStop && <StopButton runId={launch.run_id} kind={launch.kind} />}
    </div>
  )
}

function ReportButton({ runId }: { runId: string }): React.JSX.Element {
  const report = useReportTrainingRun()
  if (report.isSuccess) {
    // Plain text, not a disabled control: it stays readable and focus-free.
    return (
      <span role="status" className="text-xs text-muted-foreground">
        Reported to TrainLoop
      </span>
    )
  }
  const label = 'Report this run to TrainLoop'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          aria-label={label}
          disabled={report.isPending}
          onClick={() => {
            report.mutate({ runId })
          }}
        >
          <Flag className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

function Launches({
  launches,
  runs,
}: {
  launches: TrainingLaunchView[]
  runs: TrainingRunDocument[]
}): React.JSX.Element {
  // Said only while a launch has nothing above it to read.
  const waiting = launchWithoutMetrics(launches, runs)
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="font-serif text-lg text-foreground">Launches</h2>
        <p className="text-sm text-muted-foreground">
          Runs started from this page.
          {waiting && ' Metrics appear above once the node writes them.'}
        </p>
      </div>
      <ul className="divide-y divide-border border border-border">
        {launches.map((launch) => (
          <LaunchRow key={launch.run_id} launch={launch} />
        ))}
      </ul>
    </section>
  )
}

function LaunchRow({ launch }: { launch: TrainingLaunchView }): React.JSX.Element {
  // The preset changes a person approved for a set run (TRA-1345) or made
  // at a grpo launch (TRA-1377), if any.
  const changes = presetChangesLine(launch.preset_changes) ?? grpoChangesLine(launch.grpo_changes ?? null)
  return (
    <li className="flex flex-wrap items-baseline gap-3 p-3">
      <Badge variant={LAUNCH_BADGE[launch.status]}>{LAUNCH_STATUS_LABEL[launch.status]}</Badge>
      <span className="font-mono text-xs">{launchLabel(launch)}</span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {launch.run_id} · {launch.launched_by}
      </span>
      {launch.session_id !== null && (
        <Link
          to={`/training?view=sessions&session=${launch.session_id}`}
          className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          From an agent session
        </Link>
      )}
      {changes !== null && (
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          Changes: {changes}
        </span>
      )}
      {launch.initial_checkpoint != null && (
        <span className="break-all font-mono text-[10px] text-muted-foreground">
          From {launch.initial_checkpoint}
        </span>
      )}
      {(launch.status === 'failed' || launch.status === 'stopped') && (
        <span className="ml-auto">
          <ReportButton runId={launch.run_id} />
        </span>
      )}
      {launch.message !== null && (
        <span className="basis-full text-sm text-muted-foreground">{launch.message}</span>
      )}
    </li>
  )
}

function RunView({
  run,
  grpo,
  servable,
  recordable,
  actions,
  notice,
  canApprove,
}: {
  run: TrainingRunDocument
  /** A grpo launch made the run (`isGrpoRun`). */
  grpo: boolean
  /** The step staff may serve when the run's own export failed (`servableStep`). */
  servable: string | null
  /** Whether staff may record the run's adapter served in the cluster (`recordableAdapter`). */
  recordable: boolean
  actions: React.JSX.Element | null
  notice: React.JSX.Element | null
  /** Staff on a team they belong to: who may serve a step, score a run again or make its model the default. */
  canApprove: boolean
}): React.JSX.Element {
  const status = STATUS_BADGE[run.status]
  return (
    <div className="space-y-10">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-2xl font-medium text-foreground">
            {run.display_name}
          </h1>
          <Badge variant={status.variant}>{status.label}</Badge>
          {actions !== null && (
            <div className="ml-auto flex flex-wrap items-center gap-3">{actions}</div>
          )}
        </div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {run.run_id} · {run.method} · {run.base_model} · Step{' '}
          {run.current_step.toLocaleString()} of {run.total_steps.toLocaleString()} ·{' '}
          {formatElapsed(run)} elapsed
          {run.resume_checkpoint != null &&
            ` · Last saved checkpoint: step ${run.resume_checkpoint.step.toLocaleString()}`}
        </p>
        <p className="text-sm text-muted-foreground">
          {run.reward_curve === null
            ? 'This page follows one supervised fine-tuning run.'
            : 'This page follows one RL training run.'}{' '}
          Trained model versions appear on the{' '}
          <Link to="/" className="underline">dashboard</Link> for now.
        </p>
        {notice !== null && <div className="pt-3">{notice}</div>}
      </div>

      {run.reward_curve !== null ? (
        <section className="space-y-6">
          <div className="space-y-1">
            <h2 className="font-serif text-lg text-foreground">Reward curve</h2>
            <p className="text-sm text-muted-foreground">
              Each point is the mean reward of one trained batch. The smoothed
              line carries the trend and the vertical ticks mark interventions.
            </p>
          </div>
          <RewardCurve run={run} points={run.reward_curve} />
        </section>
      ) : (
        <section className="space-y-6">
          <div className="space-y-1">
            <h2 className="font-serif text-lg text-foreground">Loss curve</h2>
            <p className="text-sm text-muted-foreground">
              Each point is the training loss of one optimizer step. Evaluation
              loss on held-out demonstrations appears as a dashed line.
            </p>
          </div>
          <LossCurve loss={run.loss} evalLoss={run.eval_loss} totalSteps={run.total_steps} />
        </section>
      )}

      {run.rollout_progress != null && run.rollout_progress.steps.length > 0 && (
        <section className="space-y-6">
          <div className="space-y-1">
            <h2 className="font-serif text-lg text-foreground">Rollouts per step</h2>
            <p className="text-sm text-muted-foreground">
              Each step needs {run.rollout_progress.needed.toString()} trainable rollouts. The chart
              counts the rollouts finished for every step, with the trainable ones in black.
            </p>
          </div>
          <RolloutProgress run={run} progress={run.rollout_progress} />
        </section>
      )}

      {run.validation != null && run.validation.length > 0 && (
        <section className="space-y-6">
          <div className="space-y-1">
            <h2 className="font-serif text-lg text-foreground">Validation reward</h2>
            <p className="text-sm text-muted-foreground">
              Each point is the mean reward over a validation's samples at the step it ran.
            </p>
          </div>
          <ValidationCurve points={run.validation} totalSteps={run.total_steps} />
        </section>
      )}

      <HealthCards run={run} />

      {(run.task !== null || run.artifacts.length > 0) && (
        <Outcome
          run={run}
          grpo={grpo}
          servable={servable}
          recordable={recordable}
          canApprove={canApprove}
        />
      )}

      {run.groups !== null && run.groups.length > 0 && run.group_size !== null && (
        <section className="space-y-6">
          <div className="space-y-1">
            <h2 className="font-serif text-lg text-foreground">Rollout groups</h2>
            <p className="text-sm text-muted-foreground">
              GRPO learns from groups of {run.group_size.toString()} rollouts on
              one prompt. The tick on each bar marks the group mean: rewards
              right of the tick are reinforced and rewards left of it are
              suppressed.
            </p>
          </div>
          <RolloutGroups key={run.run_id} groups={run.groups} />
        </section>
      )}
    </div>
  )
}

/**
 * Staff-only "Run training" and the data menu ("Upload data", "Held-out
 * tests") controls, and "Continue run" when
 * the run shown has a saved checkpoint to continue from (TRA-1430); the api
 * 403s everyone else regardless.
 */
function LaunchButton({
  launches,
  targets,
  demo,
  continuation,
}: {
  launches: TrainingLaunchView[]
  targets: TrainingLaunchTargetView[]
  demo: boolean
  continuation: GrpoContinuation | null
}): React.JSX.Element {
  // Which dialog is open: a fresh launch, or the shown run's continuation.
  const [open, setOpen] = useState<'fresh' | 'continue' | null>(null)
  // A demo team's run trains on nothing, so neither listing is read for it.
  const sets = useTrainingSetOptions(!demo)
  const grpo = useTrainingGrpoOptions(!demo)
  const sources = { sets: sets.data?.sets, grpo: grpo.data?.datasets }
  const blocked = launchBlockedReason(launches, sources, demo)
  const answered = launchSourcesAnswered(sources, demo)
  const settled = !sets.isLoading && !grpo.isLoading
  // While a run is live the status strip under the title says so; the
  // title row only has room for the reason nothing else explains.
  const running = launches.some((launch) => launch.status === 'running')
  return (
    <div className="flex flex-wrap items-center gap-3">
      {blocked !== null && !running && (
        <span className="text-sm text-muted-foreground">{blocked}</span>
      )}
      {sets.isError && (
        <span className="text-sm text-destructive">The data did not load.</span>
      )}
      {grpo.isError && (
        <span className="text-sm text-destructive">The GRPO datasets did not load.</span>
      )}
      {/* A demo team trains nothing from a set and builds no dataset, so it
          gets neither the upload nor the held-out tests. */}
      {!demo && <TrainingDataMenu />}
      {continuation !== null && (
        <Button
          variant="outline"
          onClick={() => {
            setOpen('continue')
          }}
          disabled={blocked !== null || grpo.data === undefined}
        >
          Continue run
        </Button>
      )}
      {/* One source failing still leaves the other to launch from. */}
      <Button
        onClick={() => {
          setOpen('fresh')
        }}
        disabled={blocked !== null || !answered}
      >
        Run training
      </Button>
      {/* Mounted once every source has answered or failed: the dialog reads
          its first picks, the default preset among them, when it mounts. */}
      {open !== null && settled && answered && (
        <LaunchRunDialog
          open
          onOpenChange={(next) => {
            if (!next) setOpen(null)
          }}
          sets={sets.data?.sets ?? []}
          advanced={sets.data}
          multiNode={sets.data?.multi_node ?? null}
          grpo={grpo.data ?? { datasets: [], validation_datasets: [], default_preset: '', presets: [] }}
          targets={targets}
          demo={demo}
          continuation={open === 'continue' ? continuation : null}
        />
      )}
    </div>
  )
}

/** The Runs tab: the newest run, the launch controls and the launches list. */
function Runs(): React.JSX.Element {
  const { data, isLoading, isError, error } = useTrainingRuns()
  const { isEmployee, meLoaded, isRestricted, impersonatingTeamId } = useTeam()
  const loading = useMinimumLoading(isLoading)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const runs = data?.runs ?? []
  const launches = data?.launches ?? []
  const targets = data?.targets ?? []
  const selected: TrainingRunDocument | undefined =
    runs.find((run) => run.run_id === selectedId) ?? runs[0]
  // Impersonation is read-only (the api 403s every mutation under it), so
  // staff launch from a team they are a member of, not one they impersonate.
  // A restricted demo team lets staff launch too: its runs are simulated
  // (services/training_demo.py) and exist to show the page's states.
  const canLaunch = meLoaded && isEmployee && impersonatingTeamId === null
  const active = launches.find((launch) => launch.status === 'running')
  // The shown run's continuation from its saved checkpoint, when it ended
  // early and saved one (TRA-1430).
  const continuation = selected === undefined ? null : grpoContinuation(selected, launches)
  const launchButton = canLaunch ? (
    <LaunchButton launches={launches} targets={targets} demo={isRestricted} continuation={continuation} />
  ) : null
  const notice =
    active !== undefined ? <ActiveLaunch launch={active} canStop={canLaunch} /> : null

  return (
    <>
      {loading && !isError && <LoadingPanel />}

      {isError && (
        <p className="text-sm text-destructive">
          Failed to load training runs: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}

      {!loading && !isError && selected === undefined && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-2xl font-medium text-foreground">Training</h1>
            {launchButton !== null && <div className="ml-auto">{launchButton}</div>}
          </div>
          {notice}
          {launches.length === 0 && (
            <EmptyState
              icon={Activity}
              title="No training runs yet"
              description="Runs appear here after a training node writes its metrics to the data bucket."
            />
          )}
        </div>
      )}

      {!loading && !isError && selected !== undefined && (
        <RunView
          run={selected}
          grpo={isGrpoRun(selected, launches)}
          servable={servableStep(selected, launches)}
          recordable={DEPLOYMENT === 'enterprise' && recordableAdapter(selected, launches)}
          notice={notice}
          canApprove={canLaunch}
          actions={
            runs.length > 1 || launchButton !== null ? (
              <>
                {runs.length > 1 && (
                  <RunPicker
                    runs={runs}
                    selectedId={selected.run_id}
                    onSelect={setSelectedId}
                  />
                )}
                {launchButton}
              </>
            ) : null
          }
        />
      )}

      {!loading && !isError && launches.length > 0 && <Launches launches={launches} runs={runs} />}
    </>
  )
}

/**
 * The URL with the open tab and session changed and every other param kept,
 * such as a local build's `vanity-org` preview.
 */
function withView(
  current: URLSearchParams,
  view: string,
  session?: string,
): URLSearchParams {
  const next = new URLSearchParams(current)
  if (view === 'sessions') next.set('view', 'sessions')
  else next.delete('view')
  if (session !== undefined) next.set('session', session)
  return next
}

/**
 * Staff also get the Sessions tab (TRA-1327). The open tab and session live
 * in the URL, so a session's link opens it.
 */
export default function TrainingPage(): React.JSX.Element {
  const { isEmployee, meLoaded, impersonatingTeamId } = useTeam()
  const [params, setParams] = useSearchParams()
  if (!meLoaded || !isEmployee) {
    return (
      <PageContainer className="space-y-10">
        <Runs />
      </PageContainer>
    )
  }
  const view = params.get('view') === 'sessions' ? 'sessions' : 'runs'
  return (
    <PageContainer>
      <Tabs
        value={view}
        onValueChange={(next) => {
          setParams((current) => withView(current, next))
        }}
      >
        <TabsList aria-label="Training views">
          <TabsTrigger value="runs">Runs</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
        </TabsList>
        <TabsContent value="runs" className="mt-8 space-y-10">
          <Runs />
        </TabsContent>
        <TabsContent value="sessions" className="mt-8">
          <SessionsTab
            selectedId={params.get('session')}
            onSelect={(sessionId) => {
              setParams((current) => withView(current, 'sessions', sessionId))
            }}
            readOnly={impersonatingTeamId !== null}
          />
        </TabsContent>
      </Tabs>
    </PageContainer>
  )
}
